import React, { useEffect, useState } from 'react';
import { Camera, X, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { api } from '../../services/apiClient';

interface TripPhoto {
  id: number;
  filename: string;
  caption: string | null;
  is_public: boolean;
  created_at: string;
  trip_date: string;
  route_name: string | null;
  url: string;
}

export const TripPhotosPanel: React.FC<{ applicationId: number }> = ({ applicationId }) => {
  const [photos, setPhotos] = useState<TripPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  useEffect(() => {
    api.get(`/wycieczki/application/${applicationId}/photos`)
      .then(r => setPhotos(r.data.photos || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [applicationId]);

  if (loading) return null;
  if (photos.length === 0) return null;

  const openLightbox = (idx: number) => setLightboxIdx(idx);
  const closeLightbox = () => setLightboxIdx(null);
  const prev = () => setLightboxIdx(i => i !== null ? (i - 1 + photos.length) % photos.length : null);
  const next = () => setLightboxIdx(i => i !== null ? (i + 1) % photos.length : null);

  return (
    <>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="bg-gradient-to-r from-violet-500 to-purple-500 px-5 py-3 flex items-center gap-2">
          <Camera size={18} className="text-white" />
          <h3 className="text-white font-bold text-sm">Zdjecia z wycieczek ({photos.length})</h3>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
            {photos.map((photo, idx) => (
              <button
                key={photo.id}
                onClick={() => openLightbox(idx)}
                className="aspect-square rounded-lg overflow-hidden bg-gray-100 hover:ring-2 hover:ring-teal-400 transition-all group relative"
              >
                <img
                  src={photo.url}
                  alt={photo.caption || 'Zdjecie z wycieczki'}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  loading="lazy"
                />
                {photo.caption && (
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-1.5">
                    <p className="text-[10px] text-white truncate">{photo.caption}</p>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIdx !== null && photos[lightboxIdx] && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={closeLightbox}>
          <button onClick={(e) => { e.stopPropagation(); closeLightbox(); }} className="absolute top-4 right-4 text-white hover:text-gray-300 z-10">
            <X size={28} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 text-white hover:text-gray-300 z-10">
            <ChevronLeft size={36} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 text-white hover:text-gray-300 z-10">
            <ChevronRight size={36} />
          </button>
          <div className="max-w-4xl max-h-[85vh] flex flex-col items-center" onClick={e => e.stopPropagation()}>
            <img
              src={photos[lightboxIdx].url}
              alt={photos[lightboxIdx].caption || ''}
              className="max-w-full max-h-[75vh] object-contain rounded-lg"
            />
            <div className="mt-3 text-center">
              {photos[lightboxIdx].caption && <p className="text-white text-sm">{photos[lightboxIdx].caption}</p>}
              <p className="text-gray-400 text-xs mt-1">
                {photos[lightboxIdx].route_name} — {new Date(photos[lightboxIdx].trip_date).toLocaleDateString('pl-PL')}
              </p>
              <p className="text-gray-500 text-xs mt-0.5">{lightboxIdx + 1} / {photos.length}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
